import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { 
  CreateTrainingProgressDto, 
  UpdateTrainingProgressDto,
  BatchProgressUpdateDto,
  BatchProgressByGroupDto,
  TrainingProgressPaginationParams
} from '../types/dtos/training';
import { createTrainingProgressService } from '../services/training-progress.service';

const TRAINING_PROGRESS_KEY = 'training-progress';

// List progress by enrollment
export const useTrainingProgressByEnrollment = (
  organizationId: string,
  enrollmentId: string,
  params?: PaginationParams
) => {
  const progressService = createTrainingProgressService(organizationId, { enrollmentId });

  return useQuery({
    queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'enrollment', enrollmentId, params],
    queryFn: () => progressService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!enrollmentId
  });
};

// Get progress summary for a training (includes summary + all progress details)
export const useTrainingProgressSummary = (
  organizationId: string,
  trainingId: string
) => {
  const progressService = createTrainingProgressService(organizationId, { trainingId });

  return useQuery({
    queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'training', trainingId, 'summary'],
    queryFn: () => progressService.getProgressSummary().then((res) => res.data),
    enabled: !!organizationId && !!trainingId
  });
};

// List progress by content
export const useTrainingProgressByContent = (
  organizationId: string,
  contentId: string,
  params?: PaginationParams
) => {
  const progressService = createTrainingProgressService(organizationId, { contentId });

  return useQuery({
    queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'content', contentId, params],
    queryFn: () => progressService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!contentId
  });
};

// Create single progress record
export const useCreateTrainingProgress = (
  organizationId: string,
  enrollmentId: string
) => {
  const queryClient = useQueryClient();
  const progressService = createTrainingProgressService(organizationId, { enrollmentId });

  return useMutation({
    mutationFn: (data: CreateTrainingProgressDto) => 
      progressService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'enrollment', enrollmentId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'training'] 
      });
    }
  });
};

// Batch update progress for multiple organizations on a specific content
export const useBatchUpdateTrainingProgress = (
  organizationId: string,
  trainingId: string,
  contentId: string
) => {
  const queryClient = useQueryClient();
  const progressService = createTrainingProgressService(organizationId, { trainingId });

  return useMutation({
    mutationFn: (data: BatchProgressUpdateDto) => 
      progressService.batchUpdateProgress(trainingId, contentId, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId] 
      });
    }
  });
};

// Update progress
export const useUpdateTrainingProgress = (
  organizationId: string,
  enrollmentId: string
) => {
  const queryClient = useQueryClient();
  const progressService = createTrainingProgressService(organizationId, { enrollmentId });

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingProgressDto }) =>
      progressService.update(id, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'enrollment', enrollmentId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'training'] 
      });
    }
  });
};

// Delete progress
export const useDeleteTrainingProgress = (
  organizationId: string,
  enrollmentId: string
) => {
  const queryClient = useQueryClient();
  const progressService = createTrainingProgressService(organizationId, { enrollmentId });

  return useMutation({
    mutationFn: (id: string) => progressService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'enrollment', enrollmentId] 
      });
    }
  });
};

// Get paginated enrollment progress for a training (for table view)
export const useTrainingEnrollmentProgress = (
  organizationId: string,
  trainingId: string,
  params?: TrainingProgressPaginationParams
) => {
  const progressService = createTrainingProgressService(organizationId, { trainingId });

  return useQuery({
    queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'training', trainingId, 'enrollments', params],
    queryFn: () => progressService.getEnrollmentProgress(trainingId, params).then((res) => res.data),
    enabled: !!organizationId && !!trainingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
};

// Batch update progress by group
export const useBatchUpdateProgressByGroup = (
  organizationId: string
) => {
  const queryClient = useQueryClient();
  const progressService = createTrainingProgressService(organizationId, {});

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: BatchProgressByGroupDto }) =>
      progressService.batchUpdateByGroup(groupId, data).then((res) => res.data),
    onSuccess: () => {
      // Invalidate all progress queries to refresh data
      queryClient.invalidateQueries({ 
        queryKey: [TRAINING_PROGRESS_KEY, organizationId] 
      });
    }
  });
};

// Get single progress by ID
export const useTrainingProgressById = (
  organizationId: string,
  enrollmentId: string,
  progressId: string,
  options?: { enabled?: boolean }
) => {
  const progressService = createTrainingProgressService(organizationId, { enrollmentId });
  const enabled = options?.enabled !== undefined ? options.enabled : (!!organizationId && !!enrollmentId && !!progressId);

  return useQuery({
    queryKey: [TRAINING_PROGRESS_KEY, organizationId, 'enrollment', enrollmentId, progressId],
    queryFn: () => progressService.findById(progressId).then((res) => res.data),
    enabled
  });
};

